import { HiArrowLeft } from "react-icons/hi";
import { FaLock, FaLockOpen } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { RouteName } from "constant/routeNames";
import LazyLoadImg from "components/LazyLoadImg";
import { useDominantColor } from "hooks/useDominantColor";

const PlaylistHero = ({ playlist, loggedInUser, sentinelRef }: any) => {
  const navigate = useNavigate();
  const songCount = playlist?.songs?.length ?? 0;
  const accentColor = useDominantColor(playlist?.artwork_url);

  return (
    <div
      className="relative overflow-hidden rounded-2xl px-8 pt-8 pb-10 max-sm:px-5 max-sm:pt-6 max-sm:pb-8"
      style={{
        background: `linear-gradient(to bottom, ${accentColor} 0%, ${accentColor}cc 45%, #131313 100%)`,
      }}
    >
      <button
        className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-black/30 text-white backdrop-blur-md transition-all duration-300 hover:bg-black/50"
        onClick={() => navigate(-1)}
      >
        <HiArrowLeft />
      </button>

      <div className="mt-8 flex items-end gap-7 max-sm:flex-col max-sm:items-start max-sm:gap-4">
        <div className="shrink-0">
          <div className="relative h-[180px] w-[180px] overflow-hidden rounded-2xl ring-1 ring-white/10 shadow-[0_20px_45px_-15px_rgba(0,0,0,0.6)] max-sm:h-[150px] max-sm:w-[150px]">
            <LazyLoadImg
              imgSrc={playlist?.artwork_url}
              imgAlt={"playlist artwork"}
              imgHeight={"100%"}
              imgWidth={"100%"}
              wrapperProps={{
                style: {
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  display: "block",
                },
              }}
              className="h-full w-full object-cover"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
              }}
            />
          </div>
        </div>

        <div className="min-w-0 pb-1">
          <div className="mb-3 flex items-center gap-2">
            <span className="h-3 w-[3px] bg-[#f30543]" />
            <span className="text-[12px] font-semibold uppercase tracking-[0.14em] text-white/60">
              Playlist
            </span>
            {loggedInUser && loggedInUser?.id === playlist?.user?.id && (
              <span className="ml-1 flex items-center gap-1 text-[12px] text-white/40">
                {playlist?.visibility === 1 ? (
                  <>
                    <FaLockOpen className="text-[10px]" /> Public
                  </>
                ) : (
                  <>
                    <FaLock className="text-[10px]" /> Private
                  </>
                )}
              </span>
            )}
          </div>

          <h1 className="font-secular truncate text-5xl font-bold tracking-tight text-white max-sm:whitespace-normal max-sm:text-[28px]">
            {playlist?.title}
          </h1>

          {playlist?.description && playlist?.description !== "undefined" && (
            <p className="mt-3 max-w-[560px] truncate text-[13px] text-white/45 max-sm:whitespace-normal">
              {playlist?.description}
            </p>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-[15px] text-white/60 max-sm:text-[13px]">
            {playlist?.user?.name && (
              <Link
                to={RouteName.UserProfile.replace(
                  "*",
                  playlist?.user?.username,
                )}
                className="flex items-center gap-2 font-semibold text-white transition-colors hover:text-[#ff5f80]"
              >
                <div className="h-[22px] w-[22px] shrink-0 overflow-hidden rounded-full">
                  <LazyLoadImg
                    imgSrc={playlist?.user?.artwork_url}
                    imgAlt="playlist user"
                    imgHeight="22px"
                    imgWidth="22px"
                    className="h-full w-full object-cover"
                    style={{ width: "22px", height: "22px", objectFit: "cover" }}
                  />
                </div>
                {playlist?.user?.name}
              </Link>
            )}

            <span className="text-white/25">•</span>
            <span>
              {songCount === 0
                ? "No Songs"
                : songCount === 1
                  ? "1 Song"
                  : `${songCount} Songs`}
            </span>
          </div>
        </div>
      </div>

      {/* sentinel used to trigger the sticky condensed header once this section scrolls out of view */}
      <div ref={sentinelRef} className="absolute bottom-0 h-px w-full" />
    </div>
  );
};

export default PlaylistHero;
